import { graphql } from '@eluve/graphql.tada';

export const DOCUMENT_STATUS = {
  DRAFT: 'draft',
  CURRENT: 'current',
  SUPERSEDED: 'superseded',
  ENTERED_IN_ERROR: 'entered-in-error',
} as const;

export type DocumentStatus =
  (typeof DOCUMENT_STATUS)[keyof typeof DOCUMENT_STATUS];

export const documentReferenceFragment = graphql(`
  fragment DocumentReference on DocumentReferences @_unmask {
    __typename
    id
    tenantId
    patientId
    appointmentId
    title
    content
    contentType
    fileId
    status
    docType
    authorUserId
    authoredAt
    meta
    createdAt
    updatedAt
  }
`);

export const getDocumentReferencesByAppointmentQuery = graphql(
  `
    query getDocumentReferencesByAppointment(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      documentReferences(
        where: {
          tenantId: { _eq: $tenantId }
          appointmentId: { _eq: $appointmentId }
          status: { _nin: ["entered-in-error"] }
        }
        orderBy: { createdAt: ASC }
      ) {
        ...DocumentReference
      }
    }
  `,
  [documentReferenceFragment],
);

export const updateDocumentReferenceStatusMutation = graphql(`
  mutation updateDocumentReferenceStatus(
    $tenantId: uuid!
    $id: uuid!
    $status: String!
  ) {
    updateDocumentReferencesByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { status: $status }
    ) {
      id
      status
    }
  }
`);

export const updateDocumentReferenceContentMutation = graphql(`
  mutation updateDocumentReferenceContent(
    $tenantId: uuid!
    $id: uuid!
    $content: String!
  ) {
    updateDocumentReferencesByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { content: $content }
    ) {
      id
      content
      updatedAt
    }
  }
`);

export const updateDocumentReferenceTitleMutation = graphql(`
  mutation updateDocumentReferenceTitle(
    $tenantId: uuid!
    $id: uuid!
    $title: String!
  ) {
    updateDocumentReferencesByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { title: $title }
    ) {
      id
      title
    }
  }
`);

export const deleteDocumentReferenceMutation = graphql(`
  mutation deleteDocumentReference($tenantId: uuid!, $id: uuid!) {
    deleteDocumentReferencesByPk(tenantId: $tenantId, id: $id) {
      id
    }
  }
`);

export const insertDocumentReferenceMutation = graphql(
  `
    mutation insertDocumentReference($object: DocumentReferencesInsertInput!) {
      insertDocumentReferencesOne(object: $object) {
        ...DocumentReference
      }
    }
  `,
  [documentReferenceFragment],
);

export const getDocumentReferenceTemplatesQuery = graphql(`
  query getDocumentReferenceTemplates {
    documentReferenceTemplates(orderBy: { createdAt: ASC }) {
      id
      name
      description
      content
      instructions
      docType
      isSystemDefault
      kind
      sourceFileId
      fieldMetadata
    }
  }
`);

export const insertDocumentReferenceTemplateMutation = graphql(`
  mutation insertDocumentReferenceTemplate(
    $object: DocumentReferenceTemplatesInsertInput!
  ) {
    insertDocumentReferenceTemplatesOne(object: $object) {
      id
      name
    }
  }
`);

export const updateDocumentReferenceTemplateMutation = graphql(`
  mutation updateDocumentReferenceTemplate(
    $id: uuid!
    $set: DocumentReferenceTemplatesSetInput!
  ) {
    updateDocumentReferenceTemplatesByPk(pkColumns: { id: $id }, _set: $set) {
      id
      name
      content
    }
  }
`);

export const deleteDocumentReferenceTemplateMutation = graphql(`
  mutation deleteDocumentReferenceTemplate($id: uuid!) {
    deleteDocumentReferenceTemplatesByPk(id: $id) {
      id
    }
  }
`);
